import { Image } from 'expo-image';
import { Stack, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { useCallback, useState } from 'react';
import { getTokenValue, YStack } from 'tamagui';

import MembershipCard, { sectionOf } from '@/components/MembershipCard';
import PSFetchError from '@/components/PSFetchError';
import { PSHeaderBackButton } from '@/components/PSHeaderButtons';
import PageScrollView from '@/components/ui/PageScrollView';
import PSLinkButton from '@/components/ui/PSLinkButton';
import PSSkeleton from '@/components/ui/PSSkeleton';
import PSText from '@/components/ui/PSText';
import type { UserMembership } from '@/src/api/authApi';
import {
  useFetchMyMemberships,
  type FetchMyMembershipsState,
} from '@/src/hooks/useFetchMyMemberships';

/**
 * ── /my-memberships — the pass you hold, what's coming, what you held ────
 *
 * `GET /me/memberships` — every membership this user has ever held, one row per
 * membership rather than per billing cycle. Three sections of the same card, in
 * the order they happen to you, and the wire says which is which: `phase` is
 * derived server-side from the status and the latest cycle, so nothing here
 * reimplements "is a cancelled membership still live" (it is, until the cycle
 * they paid for ends). A card opens that membership's payments.
 *
 * The current/past split is the SECTION's job, not the card's — which is why a
 * card never prints the word "Active": the heading above it already did, and the
 * ledger row says what happens next.
 * ─────────────────────────────────────────────────────────────────────────
 */

// ── The states around the list ───────────────────────────────────────────

// A card is a filled block, so one bone IS a card — a bone already wears the
// card's own corner. This is the height of a title over a ledger row.
const CARD_BONE_HEIGHT = 128;

function MembershipsSkeleton({ count = 2 }: { count?: number }) {
  return (
    <PSSkeleton>
      <YStack alignSelf="stretch" gap="$lg">
        {Array.from({ length: count }, (_, i) => (
          <PSSkeleton.Bone
            key={i}
            style={{ width: '100%', height: CARD_BONE_HEIGHT }}
          />
        ))}
      </YStack>
    </PSSkeleton>
  );
}

/**
 * Never held one. An empty list is not a failure, so it gets the lost cat rather
 * than the no-internet one — same treatment as profile's empty reservations.
 * "Rectify that" goes to the Access Pass page, the only place a pass is sold.
 */
function NoMembershipsScreen() {
  const router = useRouter();

  return (
    <YStack items="center" gap="$space.sm" grow={1} justifyContent="center">
      <Image
        source={require('@/assets/images/not-found-cat.png')}
        style={{ width: '100%', height: 200 }}
      />
      <PSText.Body2 color="$textDark" textAlign="center">
        Looks like you&apos;ve missed out on being an Access Pass member.
      </PSText.Body2>
      <PSLinkButton
        title="Rectify that"
        onPress={() => router.push('/memberships')}
      />
    </YStack>
  );
}

// ── The list ─────────────────────────────────────────────────────────────

/**
 * The sections, in the order they happen to you: what you have, what replaces it,
 * what's behind you. Keyed by the wire's own `phase`, so grouping is a filter
 * rather than a rule this screen owns.
 *
 * At most one row is ever `current` — hence the singular heading. `past` is the
 * one that stacks.
 */
const SECTIONS: { phase: UserMembership['phase']; heading: string }[] = [
  { phase: 'current', heading: 'Current membership' },
  { phase: 'upcoming', heading: 'Upcoming membership' },
  { phase: 'past', heading: 'Past memberships' },
];

/**
 * The page body, branched on the fetch. All four states replace the WHOLE body —
 * unlike `app/profile`, where only the reservations section sits behind its fetch
 * — because the list is all this page is.
 */
function MembershipsList({ state }: { state: FetchMyMembershipsState }) {
  const router = useRouter();

  if (state.status === 'loading') return <MembershipsSkeleton />;

  if (state.status === 'error')
    return <PSFetchError message={state.error.message} onRetry={state.retry} />;

  if (state.memberships.length === 0) return <NoMembershipsScreen />;

  const groups = SECTIONS.map((section) => ({
    ...section,
    rows: state.memberships.filter((m) => sectionOf(m) === section.phase),
  })).filter((group) => group.rows.length > 0);

  // A heading tells one section from another. With only one on the page there's
  // nothing to tell it from, and the screen title already named it.
  const labelled = groups.length > 1;

  return (
    <YStack gap="$xxl">
      {groups.map((group) => (
        <YStack key={group.phase} gap="$lg">
          {labelled && <PSText.H2 color="$textDark">{group.heading}</PSText.H2>}
          {group.rows.map((membership) => (
            <MembershipCard
              key={membership.id}
              membership={membership}
              onPress={() => router.push(`/my-memberships/${membership.id}`)}
            />
          ))}
        </YStack>
      ))}
    </YStack>
  );
}

// ── The screen ───────────────────────────────────────────────────────────

export default function MyMembershipsScreen() {
  const state = useFetchMyMemberships();
  // Pulled off the state because that object is rebuilt every render while
  // `refresh` itself is stable — so the handler below is too.
  const { refresh } = state;
  const [isRefreshing, setIsRefreshing] = useState(false);

  const onRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await refresh();
    setIsRefreshing(false);
  }, [refresh]);

  return (
    <>
      <Head>
        <title>My memberships · Putting Scene</title>
      </Head>
      <Stack.Screen
        options={{
          headerTransparent: false,
          headerTitle: () => (
            <PSText.H1 color="$textDark" textAlign="center">
              My memberships
            </PSText.H1>
          ),
          headerStyle: { backgroundColor: getTokenValue('$background') },
          headerLeft: () => <PSHeaderBackButton />,
          unstable_headerLeftItems: () => [
            {
              type: 'custom',
              element: <PSHeaderBackButton />,
              hidesSharedBackground: true,
            },
          ],
        }}
      />
      <PageScrollView
        headerTransparent={false}
        isRefreshing={isRefreshing}
        onRefresh={onRefresh}
        // contentContainerStyle={{ flex: 1 }}
      >
        <MembershipsList state={state} />
      </PageScrollView>
    </>
  );
}
